// The organisation the customer app acts in, outside React so the API client can read it (WEB-F-SH-02).
// Remembered per browser and per user, in localStorage (an organisation id, not a secret).
type Listener = (orgId: string | undefined) => void;

let current: string | undefined;
const listeners = new Set<Listener>();
const key = (userId: string) => `cv.org.${userId}`;

export function currentOrgId(): string | undefined {
  return current;
}

export function setCurrentOrgId(orgId: string | undefined): void {
  if (orgId === current) return;
  current = orgId;
  for (const l of listeners) l(orgId);
}

export function subscribeOrg(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function rememberedOrg(userId: string): string | undefined {
  try {
    return localStorage.getItem(key(userId)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberOrg(userId: string, orgId: string): void {
  try {
    localStorage.setItem(key(userId), orgId);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
}

/** The address parameter that carries the organisation to another region's origin (WEB-SRV-BOOT-04). */
export const ORG_PARAM = "org";

/**
 * The organisation the address names, if the user is a member of it, and the parameter taken off the address so a
 * reload or a shared link does not pick it again.
 */
export function takeOrgFromAddress(
  memberships: readonly { org_id: string }[],
  loc: Location = window.location,
  history: History = window.history,
): string | undefined {
  const url = new URL(loc.href);
  const named = url.searchParams.get(ORG_PARAM);
  if (named === null) return undefined;
  url.searchParams.delete(ORG_PARAM);
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  return memberships.some((m) => m.org_id === named) ? named : undefined;
}

/** The remembered organisation if the user is still a member of it, otherwise the first membership. */
export function pickOrg(
  memberships: readonly { org_id: string }[],
  remembered: string | undefined,
): string | undefined {
  if (remembered && memberships.some((m) => m.org_id === remembered)) return remembered;
  return memberships[0]?.org_id;
}
